import { faChevronLeft, faChevronRight, faPlus, faSearch } from '@fortawesome/pro-light-svg-icons'
import { ButtonPrimary } from '../../../../../../../../baseComponents/Button'
import PermissionChecker from '../../../../../../../../baseComponents/PermissionChecker'
import TwinIcon from '../../../../../../../../baseComponents/TwinIcon'
import { getUserPermissions } from '../../../../../../../../utils/reducers/getters'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { CustomSelectWithSearchBar } from '../../../../../../../../forms/CustomSelect'
import { useCallback, useMemo, useState } from 'react'
import { getBasePath } from '../../../../../../../../utils/globals/link'
import { ReduxTaskCFields } from '..'
import useEditDeleteModal from '../../../../../../../../utils/hooks/useEditDeleteModal'
import ModalCreateTaskWithHelper from '../../../../../../../../specificComponents/Task/ModalCreateTaskWithHelper'
import { TaskCFieldModelType } from '@teinor/erp/types/company/task/taskCField'
import { FormElement } from '../../../../../../../../forms/FormRenderer/types'
import { TaskModelType } from '@teinor/erp/types/company/task'
import { OnSubmit } from '../../../../../../../../forms/TwinForm/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import CustomSelectColor from '../../../../../../../../forms/CustomSelect/CustomSelectColor'

type TaskCFieldFlowHeaderProps = TaskCFieldFlowSelectorProps & TaskCFieldFlowHeaderLogicProps & {
    TaskCFieldTypeId: number
    customField: TaskCFieldModelType
}

const TaskCFieldFlowHeader: React.FC<TaskCFieldFlowHeaderProps> = ({ TaskCFieldTypeId, getData, TaskCFieldTypes, customField, ...logic }) => {
    const { openModal, setOpenModal, myOnSubmit, setFieldId, fieldId } = useTaskCFieldFlowHeaderLogic({ getData, customField })
    const userPermissions = getUserPermissions('task.main')

    const extraField: FormElement<keyof TaskModelType>[] = [
        {
            name: 'TaskCFields',
            component: 'Custom',
            render: () => <CustomSelectColor items={dictionaryComplexFromJsonArr(customField?.TaskCFieldValues || [])} label={customField.name} required={true} onChange={(value) => setFieldId(value)} value={fieldId} />
        }
    ]

    return (
        <div className='table_header flex justify-between items-center mb-22 flex-wrap gap-10'>
            <TaskCFieldFlowSelector TaskCFieldTypes={TaskCFieldTypes} {...{ ...logic }} />
            <div className='flex'>
                <PermissionChecker userPermissions={userPermissions} permission={'create'}>
                    <ButtonPrimary onClick={() => setOpenModal({ type: 'edit', allRowData: {} })}>
                        <TwinIcon icon={faPlus} className='mr-8' />
                        <TwinTrans transKey='newTask'>Nueva tarea</TwinTrans>
                    </ButtonPrimary>
                </PermissionChecker>
                {openModal?.type === 'edit' ? <ModalCreateTaskWithHelper onSubmit={myOnSubmit} setOpened={() => setOpenModal(null)} extraFields={extraField} allRowData={openModal?.allRowData} userPermissions={userPermissions || undefined} notNavigate={true} /> : null}
            </div>
        </div>
    )
}

interface TaskCFieldFlowHeaderLogicProps {
    getData: () => Promise<void> 
    customField: TaskCFieldModelType
}
const useTaskCFieldFlowHeaderLogic = ({ getData, customField }: TaskCFieldFlowHeaderLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const [fieldId, setFieldId] = useState<number>(customField.TaskCFieldValues?.[0]?.id || 0)
    const customFieldId = customField?.id

    const myOnSubmit: OnSubmit = useCallback(async(task: any) => {
        if (task && fieldId) {
            await twinFetchPostJSON('/api/app/task/assignCustomFieldValueToTask', { TaskCFieldValueId: fieldId, TaskCFieldId: customFieldId, TaskId: task.id })
        }
        getData()
    }, [getData, customFieldId, fieldId])

    return { openModal, setOpenModal, setFieldId, myOnSubmit, fieldId }
}

interface TaskCFieldFlowSelectorProps extends TaskCFieldFlowSelectorLogicProps { }

const TaskCFieldFlowSelector: React.FC<TaskCFieldFlowSelectorProps> = ({ TaskCFieldTypes, FieldTypeId, ...rest }) => {
    const dictTaskCFieldTypes = dictionaryComplexFromJsonArr(TaskCFieldTypes || [])
    const { linkNextTaskCFieldType, linkPrevTaskCFieldType, onSelectTaskCFieldType } = useTaskCFieldFlowSelectorLogic({ TaskCFieldTypes: dictTaskCFieldTypes, FieldTypeId, ...rest })
    return (
        <div className='flex items-center'>
            <Link className='cursor-pointer hover:text-green-21 flex items-center' to={linkPrevTaskCFieldType}><TwinIcon className='h-20' icon={faChevronLeft} /></Link>
            <Link className='cursor-pointer hover:text-green-21 ml-10 flex items-center' to={linkNextTaskCFieldType}><TwinIcon className='h-20' icon={faChevronRight} /></Link>
            <div className='toolbar-label  text-left text-gray-51 capitalize mx-10 mt-1'>
                <h1 className='text-gray-51 text-18'>
                    <TwinTrans transKey={'board'}>Tablero</TwinTrans>:  {dictTaskCFieldTypes?.[FieldTypeId]?.name}
                </h1>
            </div>
            <CustomSelectWithSearchBar className='task_board_custom_field_flow_selector mr-24' label='' items={dictTaskCFieldTypes} onChange={onSelectTaskCFieldType} icon={faSearch} fieldName='name' />
        </div>
    )
}

interface TaskCFieldFlowSelectorLogicProps extends ReduxTaskCFields {
    TaskCFieldTypes: TwinDictionary
    FieldTypeId: number
}

const useTaskCFieldFlowSelectorLogic = ({ TaskCFieldTypes, FieldTypeId }: TaskCFieldFlowSelectorLogicProps) => {
    const navigate = useNavigate()
    const TaskCFieldTypesStr = JSON.stringify(TaskCFieldTypes)
    const positionsCPT = useMemo(() => {
        const myTaskCFieldTypes = JSON.parse(TaskCFieldTypesStr)
        let result: TwinDictionary = {
            byPos: {},
            byId: {},
            lastPos: 0
        }
        let pos = 0
        for (const key in myTaskCFieldTypes) {
            result.byPos[pos] = key
            result.byId[key] = pos
            pos++
        }
        result.lastPos = pos - 1
        return result
    }, [TaskCFieldTypesStr])

    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'taskFlow')
    const nextTaskCFieldType = useCallback(() => {
        const posId = positionsCPT.byId[FieldTypeId]
        const parsed = positionsCPT.byPos[posId + 1] || positionsCPT.byPos[0]
        return `${basePath}/${parsed}/main`
    }, [positionsCPT, FieldTypeId, basePath])

    const prevTaskCFieldTyp = useCallback(() => {
        const posId = positionsCPT.byId[FieldTypeId]
        const parsed = positionsCPT.byPos[posId - 1] || positionsCPT.byPos[positionsCPT.lastPos]
        return `${basePath}/${parsed}/main`
    }, [positionsCPT, FieldTypeId, basePath])

    const onSelectTaskCFieldType = useCallback((id: number) => {
        navigate(`${basePath}/${id}/main`)
    }, [navigate, basePath])

    return { linkNextTaskCFieldType: nextTaskCFieldType(), linkPrevTaskCFieldType: prevTaskCFieldTyp(), onSelectTaskCFieldType }
}


export default TaskCFieldFlowHeader